import { useState, useEffect, useCallback } from 'react';
import { useGameState } from './useGameState';
import toast from 'react-hot-toast';

export function usePedometer(walletAddress: string | null) {
  const [steps, setSteps] = useState(0);
  const [isAvailable, setIsAvailable] = useState(false);
  const { gameState, updateGameState } = useGameState(walletAddress);

  // Vérifier si l'API est disponible
  useEffect(() => {
    if ('DeviceMotionEvent' in window) {
      setIsAvailable(true);
    } else {
      console.warn('Pedometer API not available');
      setIsAvailable(false);
    }
  }, []);

  // Détecter les pas
  const handleStep = useCallback(() => {
    setSteps(prev => {
      const newSteps = prev + 1;
      
      // Récompenser tous les 100 pas
      if (newSteps % 100 === 0) {
        const reward = Math.floor(newSteps / 100) * 50; // 50 coins par 100 pas
        updateGameState({
          balance: (gameState.balance || 0) + reward,
          experience: (gameState.experience || 0) + 10 // 10 XP par 100 pas
        });
        toast.success(`+${reward} coins pour ${newSteps} pas !`);
      }
      
      return newSteps;
    });
  }, [gameState.balance, gameState.experience, updateGameState]);

  // Configurer la détection des mouvements
  useEffect(() => {
    if (!isAvailable) return;

    let lastTime = 0;
    let lastX = 0;
    let lastY = 0;
    let lastZ = 0;
    const threshold = 10; // Seuil de sensibilité
    const timeThreshold = 200; // Temps minimum entre les pas (ms)

    const handleMotion = (event: DeviceMotionEvent) => {
      const currentTime = new Date().getTime();
      const timeDiff = currentTime - lastTime;

      if (timeDiff < timeThreshold) return;

      const acceleration = event.accelerationIncludingGravity;
      if (!acceleration) return;

      const x = acceleration.x || 0;
      const y = acceleration.y || 0;
      const z = acceleration.z || 0;

      const movement = Math.abs(x + y + z - lastX - lastY - lastZ);

      if (movement > threshold) {
        handleStep();
        lastTime = currentTime;
      }

      lastX = x;
      lastY = y;
      lastZ = z;
    };

    window.addEventListener('devicemotion', handleMotion);

    return () => {
      window.removeEventListener('devicemotion', handleMotion);
    };
  }, [isAvailable, handleStep]);

  // Réinitialiser les pas à minuit
  useEffect(() => {
    const now = new Date();
    const night = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1
    );
    const msToMidnight = night.getTime() - now.getTime();

    const timeout = setTimeout(() => {
      setSteps(0);
    }, msToMidnight);

    return () => clearTimeout(timeout);
  }, []);

  return {
    steps,
    isAvailable,
    stepsToNextReward: 100 - (steps % 100)
  };
}